import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { Grid, Stack, TypographyProps } from '@mui/material';

import { getQuizById, getQuizState } from '../../../store/QuizSlice';
import {
  getSubmissionsForQuizHistory,
  getSubmissionState,
  loadMoreSubmissionsForQuizHistory,
} from '../../../store/SubmissionSlice';
import { useAppSelector } from '../../../hooks';
import MobileHeader from '../../../components/Mobile/Header';
import QuizHistoryJumbotron from './components/QuizHistoryJumbotron';
import DetailViewCard from '../../../components/Card/DetailViewCard';
import SubmissionListItem from './components/SubmissionListItem';

const titleProps: TypographyProps = {
  variant: 'caption',
  className: 'ellipsis-text',
  sx: {
    color: 'text.primary',
  },
};

const descriptionProps: TypographyProps = {
  className: 'ellipsis-text',
  variant: 'subtitle2',
  fontWeight: 600,
  sx: {
    color: 'text.primary',
  },
};

const intialState = {
  filterData: {
    limit: 30,
    offset: 0,
  },
};

const MyQuizHistorySubmissions: React.FC = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Store
  const quizState = useAppSelector(getQuizState);
  const submissionState = useAppSelector(getSubmissionState);

  const quizData = quizState.quiz;
  const quizTopicData = quizData && quizData.topic ? quizData.topic : undefined;
  const quizSubTopics =
    quizData && quizData.subTopics && quizData.subTopics.length
      ? quizData.subTopics
      : [];

  let subTopicText = '';

  quizSubTopics.forEach((quizSubTopic, index) => {
    const subTopicName = quizSubTopic.subTopic?.name || '';

    if (!subTopicText.includes(subTopicName)) {
      if (index > 0) {
        subTopicText += ', ';
      }

      subTopicText += subTopicName;
    }
  });

  // State
  const [state, setState] = useState(intialState);

  useEffect(() => {
    if (params.quizId) {
      dispatch(getQuizById(params.quizId));
      dispatch(
        getSubmissionsForQuizHistory({
          ...state.filterData,
          quizId: params.quizId,
        })
      );
    }
  }, []);

  // METHODS
  const handleLoadMore = () => {
    const newOffset = submissionState.list.length;

    if (params.quizId) {
      dispatch(
        loadMoreSubmissionsForQuizHistory({
          ...state.filterData,
          quizId: params.quizId,
        })
      );

      setState((prevState) => ({
        ...prevState,
        filterData: {
          ...prevState.filterData,
          offset: newOffset,
        },
      }));
    }
  };

  return (
    <div>
      <MobileHeader
        leftContents={{
          type: 'backButton',
          iconButtonProps: {
            onClick: () => navigate(-1),
          },
        }}
      />
      <QuizHistoryJumbotron isBackButtonVisible />
      <div style={{ padding: '30px 15px' }}>
        <div style={{ marginBottom: '30px' }}>
          <Grid
            container
            spacing={2}
            alignItems="center"
            style={{ maxWidth: '1300px' }}
          >
            {quizTopicData && (
              <>
                <Grid item xs={12} sm={4} md={3} lg={3}>
                  <DetailViewCard
                    title="Subject"
                    titleProps={titleProps}
                    description={quizTopicData.subject}
                    descriptionProps={descriptionProps}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3} lg={3}>
                  <DetailViewCard
                    title="Topic"
                    titleProps={titleProps}
                    description={quizTopicData.name}
                    descriptionProps={descriptionProps}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3} lg={3}>
                  <DetailViewCard
                    title="Sub Topics"
                    titleProps={titleProps}
                    description={subTopicText}
                    descriptionProps={{
                      ...descriptionProps,
                      title: subTopicText,
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </div>
        <div style={{ marginBottom: '30px' }}>
          {submissionState.list.map((submission, index) => (
            <SubmissionListItem
              key={index}
              attempt={index + 1}
              submission={submission}
              onViewClicked={() =>
                navigate(
                  `${process.env.PUBLIC_URL}/dashboard/quiz-history/questions/${submission._id}`
                )
              }
            />
          ))}
          {submissionState.count > submissionState.list.length && (
            <Stack
              direction="row"
              justifyContent="center"
              style={{ marginBottom: '30px' }}
            >
              <LoadingButton
                loading={submissionState.status === 'loading'}
                onClick={handleLoadMore}
              >
                Load More
              </LoadingButton>
            </Stack>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyQuizHistorySubmissions;
